import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { first, mergeMap, switchMap } from 'rxjs/operators';
import { MsalAuthSelectors } from 'src/app/features/msal-auth';
import { environment } from 'src/environments/environment';
import { DeviceGroupSelectors } from '../features/device-group';
import { PackageUsersSelectors } from '../features/package-users';
import { PackagesSelectors } from '../features/packages';
import { RootStoreState } from '../store';
import { buildCorrelationHeaders } from './correlation-headers';

@Injectable()
export class BaseUrlInterceptor implements HttpInterceptor {
  ignoreUrlStartsWith: string[] = ['favicon.ico', '../../assets/material-icons', 'assets/', './assets/'];

  constructor(private readonly store: Store<RootStoreState.State>) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.ignoreUrlStartsWith.some((url) => request.url.startsWith(url))) {
      return next.handle(request);
    }

    // Not routed through buildCorrelationHeaders: this branch's header set is genuinely
    // different, and the uploader's keepalive path never hits signalr, so there's no drift risk.
    if (request.url.startsWith('signalr')) {
      return combineLatest([
        this.store.pipe(select(PackageUsersSelectors.getPackageUserState)),
        this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
      ]).pipe(
        first(),
        switchMap(([packageUser, packageGuid]) =>
          next.handle(
            request.clone({
              url: `${environment.apiBaseUrl}${request.url}`,
              headers: request.headers
                .set(
                  `x-ms-client-principal-id`,
                  packageUser.activePackageUser.packageUserId.toString()
                )
                .set(`package-guid`, `${packageGuid}`)
                .set(`auth-settings`, `user-bearer`),
            })
          )
        )
      );
    }

    return combineLatest([
      this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
      this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
      this.store.pipe(select(DeviceGroupSelectors.getDeviceCode)),
      this.store.pipe(select(PackagesSelectors.getActivePackageId)),
      this.store.pipe(select(MsalAuthSelectors.getIdTokenClaims)),
    ]).pipe(
      first(),
      mergeMap(([packageUserGuid, packageGuid, deviceCode, packageId, idTokenClaims]) => {
        const correlationHeaders = buildCorrelationHeaders({
          packageGuid,
          packageUserGuid,
          deviceCode,
          packageId,
          idTokenClaims,
        });

        let headers = request.headers;
        for (const [key, value] of Object.entries(correlationHeaders)) {
          headers = headers.set(key, value);
        }

        return next.handle(
          request.clone({
            url: `${environment.apiBaseUrl}${request.url}`,
            headers,
          })
        );
      })
    );
  }
}
